import React, {useMemo, useState} from 'react';
import {Button, FormGroup, Input, Label} from 'reactstrap';
import {FaPlus} from 'react-icons/fa';
import {useBoolean} from 'usehooks-ts';

import {WorkflowRoleMasterFormModal, WorkflowRoleMasterTable} from './components';
import useLoader from 'hooks/useLoader';
import {useQueryClient} from '@tanstack/react-query';
import {useAddMasterRole, useDeleteMasterRole, useMasterRole} from './queries';
import {useMasterMenu} from '../WorkflowMenuMasterConfig/queries';
import {RoleMaster, RoleMasterForm} from '../../../models/role';
import {extractAccessToken} from '../../../helpers/decodeLocalStorage';
import Swal from 'sweetalert2';
import axios, {AxiosError} from 'axios';
import {MasterCompany} from '../../../models/company/MasterCompany';
import Select from 'react-select';
import {useAdminCompanies} from '../../../hooks';

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
        ? error.message
        : 'Unknown error occurred.';

export const WorkflowRoleMasterConfig = () => {
    const loader = useLoader();
    const queryClient = useQueryClient();

    const { data: masterRole } = useMasterRole();

    const { data: masterMenu } = useMasterMenu();
    const masterMenuMap = useMemo(
        () =>
            masterMenu
                ? new Map(masterMenu.map(menu => [menu.id, menu]))
                : undefined,
        [masterMenu]
    );

    const masterRoleForms = useMemo(() => {
        if (!masterMenuMap || !masterRole) {
            return undefined;
        }

        const output = new Map<string, RoleMasterForm>();
        masterRole.forEach(role => {
            const menu = masterMenuMap.get(role.menu_id);
            if (!menu) {
                return;
            }

            const masterRoleForm = output.get(role.sid + role.id);
            if (!masterRoleForm) {
                output.set(role.sid + role.id, {
                    sid: role.sid,
                    id: role.id,
                    roleDetail: role.role_detail,
                    allowMenus: [menu]
                });
            } else {
                masterRoleForm.allowMenus.push(menu);
            }
        });

        return Array.from(output.values());
    }, [masterMenuMap, masterRole]);

    const { adminCompanies, isAllowedAllCompanies } = useAdminCompanies();
    const adminCompanyOptions = useMemo(() => {
        if (!adminCompanies) {
            return undefined;
        }

        const options: MasterCompany[] = [
            {
                sid: 'all',
                company_name: 'เลือกทั้งหมด'
            } as MasterCompany
        ];

        if (isAllowedAllCompanies) {
            options.push({
                sid: '*',
                company_name: '* : SUPER_ADMIN'
            } as MasterCompany);
        }

        return options.concat(adminCompanies);
    }, [adminCompanies, isAllowedAllCompanies]);

    const [selectedCompany, setSelectedCompany] = useState<MasterCompany>();
    const [searchRoleId, setSearchRoleId] = useState('');

    const filteredMasterRoleForms = useMemo(
        () =>
            masterRoleForms?.filter(masterRoleForm => {
                const isMatchedSID =
                    selectedCompany && selectedCompany.sid !== 'all'
                        ? masterRoleForm.sid === selectedCompany.sid
                        : isAllowedAllCompanies ||
                          adminCompanies?.some(
                              adminCompany =>
                                  adminCompany.sid === masterRoleForm.sid
                          );
                const isMatchedRoleId = masterRoleForm.id
                    .toLowerCase()
                    .includes(searchRoleId.toLowerCase());

                return isMatchedSID && isMatchedRoleId;
            }),
        [
            isAllowedAllCompanies,
            adminCompanies,
            masterRoleForms,
            selectedCompany,
            searchRoleId
        ]
    );

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();

    const [selectedEditRoleMasterForm, setSelectedEditRoleMasterForm] =
        useState<RoleMasterForm>();
    const isOpenEditModal = !!selectedEditRoleMasterForm;

    const { mutate: addMasterRole, mutateAsync: addMasterRoleAsync } =
        useAddMasterRole();
    const { mutate: deleteMasterRole, mutateAsync: deleteMasterRoleAsync } =
        useDeleteMasterRole();

    const handleSubmitAddRoleMaster = (formValues: RoleMasterForm) => {
        const payload = extractAccessToken();

        const createdBy = payload.citizen_id || payload.username;
        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    role_detail: formValues.roleDetail,
                    menu_id: menu.id,
                    created_by: createdBy
                }) as RoleMaster
        );

        loader.show();

        requests.forEach(request =>
            addMasterRole(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    loader.hide();
                    toggleIsOpenCreateModal();

                    await queryClient.invalidateQueries({
                        queryKey: ['master-role']
                    });
                }
            })
        );
    };

    const handleEditRoleMasterForm = (roleMasterForm: RoleMasterForm) => {
        setSelectedEditRoleMasterForm(roleMasterForm);
    };

    const handleCloseEditModal = () => {
        setSelectedEditRoleMasterForm(undefined);
    };

    const handleSubmitEditRoleMaster = async (formValues: RoleMasterForm) => {
        const currentMasterRoleForm = masterRoleForms?.find(
            masterRoleForm => masterRoleForm.id === formValues.id
        );
        if (!currentMasterRoleForm) {
            return;
        }

        const currentAllowedMenus = currentMasterRoleForm.allowMenus;

        // find remove menus
        const newAllowedMenuIdSet = new Set(
            formValues.allowMenus.map(menu => menu.id)
        );
        const removeRoleMasterRequests = currentAllowedMenus
            .filter(menu => !newAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id
                    }) as RoleMaster
            );

        // find newly added munus
        const payload = extractAccessToken();
        const currentAllowedMenuIdSet = new Set(
            currentAllowedMenus.map(menu => menu.id)
        );
        const addRoleMasterRequests = formValues.allowMenus
            .filter(menu => !currentAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id,
                        role_detail: formValues.roleDetail,
                        created_by: payload.citizen_id || payload.username
                    }) as RoleMaster
            );

        loader.show();

        try {
            await Promise.all([
                ...removeRoleMasterRequests.map(request =>
                    deleteMasterRoleAsync(request)
                ),
                ...addRoleMasterRequests.map(request =>
                    addMasterRoleAsync(request)
                )
            ]);

            loader.hide();

            handleCloseEditModal();

            await Swal.fire({
                title: 'Success!',
                icon: 'success'
            });
            await queryClient.invalidateQueries({ queryKey: ['master-role'] });
        } catch (error) {
            loader.hide();

            await Swal.fire({
                title: 'Error!',
                icon: 'error',
                text: getErrorMessage(error)
            });
        }
    };

    const handleDeleteRoleMaster = async (formValues: RoleMasterForm) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${formValues.id}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    menu_id: menu.id
                }) as RoleMaster
        );

        loader.show();

        requests.forEach(request =>
            deleteMasterRole(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    loader.hide();
                    await queryClient.invalidateQueries({
                        queryKey: ['master-role']
                    });
                }
            })
        );
    };

    return (
        <div className="master-data-content flex flex-1 flex-col">
            <div className="flex justify-between">
                <div className="trapezoid-head w-fit">
                    <span>Workflow Role Master Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="flex items-center gap-1 self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box flex flex-1 flex-col">
                {/* Search */}
                <div className="flex justify-between">
                    <div className="trapezoid-head w-fit">
                        <span>Search</span>
                    </div>
                </div>
                <div className="mat-box flex flex-col">
                    <FormGroup className="w-full md:w-1/3">
                        <Label for="sid">Company</Label>
                        <Select
                            options={adminCompanyOptions}
                            value={selectedCompany}
                            getOptionLabel={(company: MasterCompany) =>
                                company.company_name
                            }
                            getOptionValue={(company: MasterCompany) =>
                                company.sid
                            }
                            onChange={setSelectedCompany}
                        />
                    </FormGroup>
                    <FormGroup className="w-full md:w-1/3">
                        <Label for="id">Role ID</Label>
                        <Input
                            type="text"
                            value={searchRoleId}
                            onChange={event =>
                                setSearchRoleId(event.target.value)
                            }
                        />
                    </FormGroup>
                </div>

                <WorkflowRoleMasterTable
                    roleMasterForms={filteredMasterRoleForms}
                    onEdit={handleEditRoleMasterForm}
                    onDelete={handleDeleteRoleMaster}
                />

                {/*  Create modal */}
                <WorkflowRoleMasterFormModal
                    title="Create Role"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddRoleMaster}
                />

                {/* Edit modal */}
                <WorkflowRoleMasterFormModal
                    disabledKeyField
                    title="Edit Role"
                    defaultValues={selectedEditRoleMasterForm}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditRoleMaster}
                />
            </div>
        </div>
    );
};
